import React from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Stack,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';

export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  width?: number | string;
  anchor?: 'right' | 'left' | 'top' | 'bottom';
}

const SidePanel: React.FC<SidePanelProps> = ({
  open,
  onClose,
  title,
  icon,
  children,
  actions,
  width = 800,
  anchor = 'right'
}) => {
  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: width,
          maxWidth: '95vw',
          backgroundColor: '#1e1e1e',
          color: '#d4d4d4',
          borderLeft: anchor === 'right' ? '1px solid #3c3c3c' : 'none',
          borderRight: anchor === 'left' ? '1px solid #3c3c3c' : 'none',
        }
      }}
      SlideProps={{
        easing: {
          enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
          exit: 'cubic-bezier(0.4, 0, 0.2, 1)'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: '#1e1e1e'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: '#252526',
            borderBottom: '1px solid #3c3c3c',
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
            {icon && (
              <Box sx={{ color: '#007acc', display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
            )}
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 500,
                color: '#cccccc',
                fontSize: '1.1rem'
              }}
            >
              {title}
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#cccccc',
              '&:hover': {
                backgroundColor: '#3c3c3c',
                color: '#ffffff'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 2.5,
            py: 2
          }}
        >
          {children}
        </Box>

        {/* Footer Actions */}
        {actions && (
          <>
            <Divider sx={{ borderColor: '#3c3c3c' }} />
            <Box
              sx={{
                backgroundColor: '#252526',
                borderTop: '1px solid #3c3c3c',
                px: 2.5,
                py: 1.5,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                flexShrink: 0
              }}
            >
              {actions}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default SidePanel;
